import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import type { Role } from "@nh/shared";
import { useAuth } from "@/features/auth/auth.context";
import { Alert, Button } from "@/shared/ui/core";

/**
 * ═══════════════════════════════════════════════════════════════════
 * ESTO ES COMODIDAD, NO SEGURIDAD.
 *
 * Lo que hace este componente es no enseñarle a alguien una pantalla
 * que no va a poder usar. Nada más. Cualquiera puede borrar esta
 * comprobación desde las herramientas del navegador, y entonces vería
 * un panel VACÍO: los datos no están aquí, están detrás de
 * `/api/admin/products`, y ahí es donde el servidor exige el rol.
 *
 * La seguridad la pone el servidor. Si algún día esta comprobación se
 * quita por error, lo que se rompe es la cortesía, no la puerta. Y al
 * revés: añadir aquí un rol nuevo NO le da acceso a nadie mientras el
 * backend no lo acepte.
 * ═══════════════════════════════════════════════════════════════════
 */
const REQUIRED_ROLE: Role = "super_admin";

export interface AdminGuardProps {
  children: ReactNode;
}

export function AdminGuard({ children }: AdminGuardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return (
      <Alert
        variant="info"
        title={t("Entra para continuar")}
        className="max-w-xl"
      >
        <p>{t("Esta sección es del panel de administración.")}</p>
        <Button
          size="sm"
          variant="secondary"
          className="mt-3"
          onClick={() => navigate("/login")}
        >
          {t("Iniciar sesión")}
        </Button>
      </Alert>
    );
  }

  if (user.role !== REQUIRED_ROLE) {
    return (
      <Alert
        variant="warning"
        title={t("Esta sección es solo para super administradores")}
        className="max-w-xl"
      >
        <p className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {t(
              "Tu cuenta no tiene ese permiso. Si crees que debería tenerlo, pídelo a quien administra el sistema.",
            )}
          </span>
        </p>
      </Alert>
    );
  }

  return <>{children}</>;
}
